/**
 * SearchSpring Autocomplete
 *
 * Replaces Horizon's native predictive search with SS-powered autocomplete.
 * Monkey-patches the predictive-search-component's search and resetSearch
 * methods to use SS Suggest, Autocomplete, and Trending APIs.
 *
 * Renders results using Horizon's existing class names and structure so that:
 * - Keyboard navigation (onSearchKeyDown) works unchanged
 * - "View all results" button appears via existing CSS rule
 * - Product cards and query pills inherit Horizon's styling
 *
 * Depends on:
 * - ss-client.js (defer IIFE) for createSSClient, formatMoney, and renderProductCard
 *   (used to build the rich Hanro cards in the autocomplete dropdown so they match
 *   the SS Collection and SS Search Results pages)
 * - ss-beacon.js (defer IIFE, optional) for Beacon 2.0 autocomplete tracking —
 *   render/impression/clickthrough events that feed the Autocomplete report (HAN-404).
 *   Tracking is skipped gracefully if the script is absent.
 * - ss-autocomplete-config JSON element for siteId, badge config, and aspect ratio
 * - predictive-search.js (module) for the component to patch
 *
 * Loaded as type="module" with fetchpriority="low".
 * Uses DOMContentLoaded to ensure predictive-search-component is defined.
 */

import { TRENDING_BLOCKLIST } from './ss-trending-blocklist.js';

// ===========================================================================
// Utilities
// ===========================================================================

function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

// Athos returns HTML-encoded text; decode before re-escaping.
function decodeEntities(str) {
  const txt = document.createElement('textarea');
  txt.innerHTML = String(str);
  return txt.value;
}

function escapeHtml(str) {
  if (!str) return '';
  return decodeEntities(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(str) {
  if (!str) return '';
  return decodeEntities(str)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;');
}

function toCents(dollars) {
  const num = parseFloat(dollars);
  return isNaN(num) ? 0 : Math.round(num * 100);
}

/**
 * Normalize a term for exclusion matching: case-insensitive, whitespace
 * collapsed, so "Bra Tops", "bra  tops" and " BRA TOPS " all compare equal.
 */
function normalizeTerm(term) {
  return String(term || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/**
 * Build the trending-exclusion matcher (ECSR-455) from two sources:
 *
 *   1. the merchant-editable theme setting — newline- and/or comma-separated,
 *      for off-brand or discontinued terms the team wants hidden;
 *   2. the bundled profanity blocklist in ss-trending-blocklist.js.
 *
 * Single-word entries match whole words anywhere in the trending term, so
 * "fuck" also kills "fuck this bra" — but never a substring, so "cum" leaves
 * the colour "Golden Cumin" alone and "butt" leaves "button front" alone.
 * Multi-word entries match as a whole phrase on word boundaries.
 */
function buildTrendingExclusions(raw) {
  const words = new Set();
  const phrases = [];

  const add = (term) => {
    const normalized = normalizeTerm(term);
    if (!normalized) return;
    if (normalized.includes(' ')) phrases.push(normalized);
    else words.add(normalized);
  };

  String(raw || '')
    .split(/[\n,]/)
    .forEach(add);
  TRENDING_BLOCKLIST.forEach(add);

  return { words, phrases };
}

/**
 * True when a trending term should be hidden. Tokens are split on whitespace
 * and stripped of edge punctuation so "sexy!" still matches "sexy", while
 * blocklist entries that contain punctuation internally ("b!tch", "s&m")
 * still compare intact.
 */
function isTrendingExcluded(term, { words, phrases }) {
  const normalized = normalizeTerm(term);
  if (!normalized) return true;

  const tokens = normalized
    .split(' ')
    .map((token) => token.replace(/^[^a-z0-9!&+*_.-]+|[^a-z0-9!&+*_.-]+$/g, ''));
  if (tokens.some((token) => token && words.has(token))) return true;

  const padded = ` ${normalized} `;
  return phrases.some((phrase) => padded.includes(` ${phrase} `));
}

// ===========================================================================
// SSAutocomplete
// ===========================================================================

const USER_ID_KEY = 'ss-userId';
const SESSION_ID_KEY = 'ss-sessionId';

class SSAutocomplete {
  #siteId;
  #config;
  #component;
  #abortController;
  #trendingCache;
  #trendingExclusions;
  #lastQuery;
  #beacon;

  constructor(config) {
    this.#config = config || {};
    this.#siteId = this.#config.siteId;
    this.#abortController = null;
    this.#trendingCache = null;
    this.#trendingExclusions = buildTrendingExclusions(this.#config.trendingExclusions);
    this.#lastQuery = '';
    this.#beacon = null;
  }

  init() {
    const component = document.querySelector('predictive-search-component');
    if (!component) {
      console.warn('[SS Autocomplete] predictive-search-component not found');
      return;
    }
    this.#component = component;
    this.#patchComponent();
    this.#listenForDialogOpen();
    this.#initBeacon();
  }

  /**
   * Beacon 2.0 tracking for the autocomplete module (HAN-404): render on every
   * dropdown result set, impressions on visible cards, clickthrough on product
   * click. Deliberately does NOT call beacon.init() — that wires the grid
   * listeners meant for the PLP/search orchestrators; the dropdown drives
   * tracking directly via trackResults()/trackClickthrough().
   */
  #initBeacon() {
    if (!window.SSBeacon || !window.createSSClient) return;

    const client = window.createSSClient({ siteId: this.#siteId });
    this.#beacon = new window.SSBeacon({
      siteId: this.#siteId,
      pageLoadId: client.pageLoadId,
      module: 'autocomplete',
    });
  }

  /**
   * Report a rendered autocomplete response to the beacon. The products list
   * is the impression root; when there are no products (suggestions-only or
   * zero results) the render event still fires — that's what the Autocomplete
   * and Zero Results reports count.
   */
  #trackBeaconResults(autocompleteData) {
    if (!this.#beacon || !autocompleteData) return;

    const container = this.#component.refs.predictiveSearchResults;
    const root =
      container.querySelector('.predictive-search-results__wrapper-products') ||
      container;
    this.#beacon.trackResults(autocompleteData, root);
  }

  // -------------------------------------------------------------------------
  // Component patching
  // -------------------------------------------------------------------------

  #patchComponent() {
    const component = this.#component;

    // Override the search method (own property assigned as class field)
    // Horizon's on:input="/search" calls this.search(event) — our override runs instead
    component.search = debounce((event) => {
      if (!event.inputType) return;

      const searchTerm = component.refs.searchInput.value.trim();

      if (!searchTerm.length) {
        component.resetSearch();
        return;
      }

      component.refs.resetButton.hidden = false;

      // Require 2+ characters before querying SS
      if (searchTerm.length < 2) return;

      this.#fetchAndRender(searchTerm);
    }, 200);

    // Override resetSearch to show trending searches instead of Horizon's
    // empty state (recently viewed products fetched via sectionRenderer).
    // Only fetch trending if the dialog is open — Horizon calls resetSearch
    // during page load (via requestIdleCallback) to preload the empty state,
    // and we don't want to make API calls when the dialog isn't visible.
    component.resetSearch = debounce(async (keepFocus = true) => {
      if (keepFocus) {
        component.refs.searchInput.focus();
      }
      component.refs.searchInput.value = '';
      component.refs.resetButton.hidden = true;
      this.#lastQuery = '';
      this.#abort();

      // Only fetch trending if the dialog is actually open
      const dialogEl = component.closest('dialog-component')?.querySelector('dialog');
      if (dialogEl?.hasAttribute('open')) {
        this.#showTrending();
      }
    }, 100);
  }

  /**
   * Listen for the search dialog to open and show trending searches.
   * We don't rely on Horizon's internal #loadEmptyState flow because it
   * only fires when recently viewed products exist. We want trending
   * to show regardless.
   */
  #listenForDialogOpen() {
    const dialog = this.#component.closest('dialog-component');
    if (!dialog) return;

    const dialogEl = dialog.querySelector('dialog');
    if (!dialogEl) return;

    // MutationObserver watches for the dialog's "open" attribute
    const observer = new MutationObserver(() => {
      if (dialogEl.hasAttribute('open') && !this.#component.refs.searchInput.value.trim()) {
        this.#showTrending();
      }
    });
    observer.observe(dialogEl, { attributes: true, attributeFilter: ['open'] });
  }

  async #showTrending() {
    try {
      const trending = await this.#fetchTrending();
      if (trending && trending.length > 0) {
        this.#renderTrending(trending);
      }
    } catch (err) {
      console.error('[SS Autocomplete] Trending fetch failed:', err);
    }
  }

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  #abort() {
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
  }

  async #fetchAndRender(searchTerm) {
    this.#abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    try {
      const [suggestData, autocompleteData] = await Promise.all([
        this.#fetchSuggestions(searchTerm, signal),
        this.#fetchAutocomplete(searchTerm, 'input', searchTerm, signal),
      ]);

      if (signal.aborted) return;

      this.#lastQuery = searchTerm;
      this.#renderResults(searchTerm, suggestData, autocompleteData);
      this.#trackBeaconResults(autocompleteData);

      // GTM dataLayer push + custom event for extensibility
      this.#pushAutocompleteQueryGTM(searchTerm, suggestData, autocompleteData);
      document.dispatchEvent(
        new CustomEvent('ss:autocomplete-rendered', {
          detail: {
            query: searchTerm,
            suggestions: suggestData,
            products: autocompleteData,
          },
        })
      );
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[SS Autocomplete]', err);
    }
  }

  /**
   * Suggest API — returns term suggestions, alternatives, and spell corrections.
   * productCount=0 because we get products from the Autocomplete API instead.
   */
  async #fetchSuggestions(term, signal) {
    const url = new URL(
      `https://${this.#siteId}.a.searchspring.io/api/suggest/query`
    );
    url.searchParams.set('siteId', this.#siteId);
    url.searchParams.set('q', term);
    url.searchParams.set('suggestionCount', '5');
    url.searchParams.set('productCount', '0');

    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Suggest API ${response.status}`);
    return response.json();
  }

  /**
   * Autocomplete API — returns product results for the autocomplete dropdown.
   * MUST use autocomplete.json (not search.json) for SS ML/reporting.
   *
   * @param {string} term - The query term (may be a suggestion, not raw input)
   * @param {string} source - Where the query came from: 'input', 'suggested', 'trending'
   * @param {string} input - What the user actually typed (for ML learning)
   * @param {AbortSignal|null} signal - AbortController signal
   */
  async #fetchAutocomplete(term, source, input, signal) {
    const url = new URL(
      `https://${this.#siteId}.a.searchspring.io/api/search/autocomplete.json`
    );
    url.searchParams.set('siteId', this.#siteId);
    url.searchParams.set('q', term);
    url.searchParams.set('source', source);
    url.searchParams.set('input', input);
    url.searchParams.set('resultsPerPage', '4');
    url.searchParams.set('resultsFormat', 'json');
    url.searchParams.set('beacon', 'true');
    url.searchParams.set('domain', window.location.href);

    // Identity params — shared keys with SSClient (ss-client.js)
    url.searchParams.set(
      'userId',
      this.#getOrCreateId(USER_ID_KEY, localStorage)
    );
    url.searchParams.set(
      'sessionId',
      this.#getOrCreateId(SESSION_ID_KEY, sessionStorage)
    );

    // pageLoadId from SSClient singleton if available
    if (window.createSSClient) {
      const client = window.createSSClient({ siteId: this.#siteId });
      url.searchParams.set('pageLoadId', client.pageLoadId);
    }

    const fetchOptions = signal ? { signal } : {};
    const response = await fetch(url, fetchOptions);
    if (!response.ok) throw new Error(`Autocomplete API ${response.status}`);
    return response.json();
  }

  /**
   * Trending API — returns popular search queries from the past 90 days.
   * Results are cached since trending data is stable within a page session.
   *
   * Exclusions (ECSR-455): the API has no server-side curation, so both the
   * bundled profanity blocklist and the merchant's "Trending searches —
   * excluded terms" setting are applied here. We over-fetch (20) and slice
   * back to the display count (6) after filtering, so exclusions don't shrink
   * the visible list. The blocklist always applies, so the over-fetch is
   * unconditional.
   */
  async #fetchTrending() {
    if (this.#trendingCache) return this.#trendingCache;

    const DISPLAY_COUNT = 6;
    const url = new URL(
      `https://${this.#siteId}.a.searchspring.io/api/suggest/trending`
    );
    url.searchParams.set('siteId', this.#siteId);
    url.searchParams.set('limit', '20');

    const response = await fetch(url);
    if (!response.ok) throw new Error(`Trending API ${response.status}`);
    const data = await response.json();
    let queries = data?.trending?.queries || [];
    queries = queries.filter((q) => {
      // Athos returns HTML-encoded text ("night &amp; day"); decode before
      // matching so merchants can type terms the way they read on screen.
      const term = decodeEntities(String(q.searchQuery || q.query || ''));
      return !isTrendingExcluded(term, this.#trendingExclusions);
    });
    queries = queries.slice(0, DISPLAY_COUNT);
    this.#trendingCache = queries;
    return queries;
  }

  #getOrCreateId(key, storage) {
    let id = storage.getItem(key);
    if (!id) {
      id = crypto.randomUUID();
      storage.setItem(key, id);
    }
    return id;
  }

  // -------------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------------

  /**
   * Render search results (suggestions + products) into the predictiveSearchResults container.
   *
   * HTML structure mirrors Horizon's native predictive-search.liquid output:
   * - Outer: .predictive-search-dropdown > .predictive-search-results__inner[data-search-results]
   * - Suggestions: .predictive-search-results__wrapper-queries with pill-style items
   * - Products: .predictive-search-results__products > .predictive-search-results__wrapper-products grid
   *
   * This ensures:
   * - Keyboard nav works (onSearchKeyDown / #allResultsItems finds our items)
   * - "View all" button shows (CSS rule uses :has([data-search-results]))
   * - Visual appearance matches native Horizon
   */
  #renderResults(searchTerm, suggestData, autocompleteData) {
    const container = this.#component.refs.predictiveSearchResults;
    const suggestions = this.#buildSuggestionsPills(suggestData, searchTerm);
    const products = autocompleteData?.results || [];
    const productHTML = this.#buildProductGrid(products);

    container.innerHTML =
      '<div id="predictive-search-results" class="predictive-search-dropdown" role="listbox" aria-expanded="true">' +
      '<div class="predictive-search-results__inner" data-search-results>' +
      suggestions +
      productHTML +
      '</div>' +
      '</div>';

    this.#attachSuggestionHoverListeners(container, searchTerm);
    this.#attachProductClickListeners(container);
    this.#resetScroll(container);
  }

  /**
   * Render trending searches into the container (shown when input is empty).
   */
  #renderTrending(queries) {
    const container = this.#component.refs.predictiveSearchResults;

    const items = queries
      .map((q) => {
        const term = q.searchQuery || q.query || '';
        if (!term) return '';
        return (
          '<li class="predictive-search-results__card--query" ref="resultsItems[]" role="option" aria-selected="false">' +
          '<a class="pills__pill predictive-search-results__pill" href="/search?q=' +
          encodeURIComponent(term) +
          '">' +
          '<span>' +
          escapeHtml(term) +
          '</span>' +
          '</a>' +
          '</li>'
        );
      })
      .join('');

    container.innerHTML =
      '<div id="predictive-search-results" class="predictive-search-dropdown" role="listbox" aria-expanded="true">' +
      '<div class="predictive-search-results__inner" data-search-results>' +
      '<div class="ss-autocomplete-trending">' +
      '<h4 class="predictive-search-results__title">Trending Searches</h4>' +
      '<ul class="predictive-search-results__list predictive-search-results__wrapper predictive-search-results__wrapper-queries list-unstyled" role="listbox">' +
      items +
      '</ul>' +
      '</div>' +
      '</div>' +
      '</div>';

    this.#resetScroll(container);
  }

  /**
   * Build query suggestion pills matching Horizon's native pill style.
   *
   * Horizon uses: .predictive-search-results__card--query with ref="resultsItems[]"
   * inside .predictive-search-results__wrapper-queries. The ref attribute makes these
   * discoverable by Horizon's #allResultsItems getter for keyboard navigation.
   */
  #buildSuggestionsPills(suggestData, searchTerm) {
    if (!suggestData) return '';

    const terms = [];

    // Add corrected query first if present (spell correction)
    const corrected = suggestData['corrected-query'];
    if (corrected && corrected !== searchTerm && !terms.includes(corrected)) {
      terms.push(corrected);
    }

    // Add the top suggestion
    if (
      suggestData.suggested?.text &&
      suggestData.suggested.text !== searchTerm &&
      !terms.includes(suggestData.suggested.text)
    ) {
      terms.push(suggestData.suggested.text);
    }

    // Add alternatives
    if (Array.isArray(suggestData.alternatives)) {
      for (const alt of suggestData.alternatives) {
        if (alt.text && !terms.includes(alt.text) && alt.text !== searchTerm) {
          terms.push(alt.text);
        }
        if (terms.length >= 5) break;
      }
    }

    if (terms.length === 0) return '';

    const items = terms
      .map(
        (term) =>
          '<li class="predictive-search-results__card--query" ref="resultsItems[]" role="option" aria-selected="false" data-suggestion-term="' +
          escapeAttr(term) +
          '">' +
          '<a class="pills__pill predictive-search-results__pill" href="/search?q=' +
          encodeURIComponent(term) +
          '">' +
          '<span>' +
          escapeHtml(term) +
          '</span>' +
          '</a>' +
          '</li>'
      )
      .join('');

    return (
      '<ul class="predictive-search-results__list predictive-search-results__wrapper predictive-search-results__wrapper-queries list-unstyled" role="listbox">' +
      items +
      '</ul>'
    );
  }

  /**
   * Build product grid matching Horizon's native predictive search product layout.
   *
   * The wrapper-products grid is 2-col mobile / 4-col desktop (existing CSS).
   * Each item has .predictive-search-results__card--product for keyboard nav
   * discovery and contains a rich <product-card> rendered by ss-client.js.
   */
  #buildProductGrid(products) {
    if (!Array.isArray(products) || products.length === 0) return '';

    const items = this.#renderProductItems(products);

    return (
      '<div class="predictive-search-results__products">' +
      '<h4 class="predictive-search-results__title">Products</h4>' +
      '<ul class="predictive-search-results__list predictive-search-results__wrapper-products list-unstyled" role="listbox">' +
      items +
      '</ul>' +
      '</div>'
    );
  }

  /**
   * Render individual product card list items.
   * Shared by #buildProductGrid (initial render) and #updateProductGrid (hover update).
   *
   * Delegates card HTML to window.renderProductCard (defined in ss-client.js) so
   * the autocomplete dropdown shows the same rich Hanro cards as the SS Collection
   * and SS Search Results pages. Each card is wrapped in the
   * predictive-search-results__card--product li for keyboard nav (ref="resultsItems[]"
   * is found by Horizon's #allResultsItems getter). SS click-tracking attrs are stored
   * on the li for the click listener to read.
   */
  #renderProductItems(products) {
    if (typeof window.renderProductCard !== 'function') {
      console.error('[SS Autocomplete] window.renderProductCard unavailable — ss-client.js failed to load');
      return '';
    }

    return products
      .slice(0, 4)
      .map((product) => {
        const productId = product.uid || product.id || '';
        const name = product.name || product.title || '';
        const priceCents = toCents(product.price);
        const cardHTML = window.renderProductCard(product, this.#config);

        return (
          '<li class="predictive-search-results__card predictive-search-results__card--product" ' +
          'role="option" aria-selected="false" ref="resultsItems[]" ' +
          'data-ss-product-id="' + escapeAttr(productId) + '" ' +
          'data-ss-product-name="' + escapeAttr(name) + '" ' +
          'data-ss-product-price="' + priceCents + '">' +
          cardHTML +
          '</li>'
        );
      })
      .join('');
  }

  /**
   * When hovering a query suggestion, fetch autocomplete products for that term
   * and update the product grid. Uses source='suggested' and the original typed
   * query as 'input' — this is critical for SS ML training.
   */
  #attachSuggestionHoverListeners(container, originalQuery) {
    const suggestions = container.querySelectorAll(
      '.predictive-search-results__card--query[data-suggestion-term]'
    );

    suggestions.forEach((li) => {
      li.addEventListener('mouseenter', async () => {
        const term = li.dataset.suggestionTerm;
        if (!term) return;

        try {
          const data = await this.#fetchAutocomplete(
            term,
            'suggested',
            originalQuery,
            null
          );
          if (data?.results) {
            this.#updateProductGrid(data.results);
            // Each hover fetch is a new API response — re-anchor the beacon
            // chain on its responseId so impressions/clicks attribute correctly
            this.#trackBeaconResults(data);
          }
        } catch (err) {
          // Silently ignore hover fetch errors — the existing products remain
        }
      });
    });
  }

  /**
   * Attach click listeners to product cards for GTM tracking.
   *
   * Listener sits on the <li> wrapper so any link click inside the rich
   * <product-card> bubbles up. Swatch buttons (button[type=button]) don't
   * navigate, so we filter to only track real link clicks.
   */
  #attachProductClickListeners(container) {
    const items = container.querySelectorAll(
      'li.predictive-search-results__card--product[data-ss-product-id]'
    );
    items.forEach((li) => {
      li.addEventListener('click', (e) => {
        if (!e.target.closest('a')) return;

        const detail = {
          query: this.#lastQuery,
          productId: li.dataset.ssProductId,
          name: li.dataset.ssProductName,
          price: li.dataset.ssProductPrice,
        };

        this.#beacon?.trackClickthrough(detail.productId);
        this.#pushAutocompleteClickGTM(detail);
        document.dispatchEvent(
          new CustomEvent('ss:autocomplete-product-click', { detail })
        );
      });
    });
  }

  /**
   * Update just the product grid (called when hovering a different suggestion).
   */
  #updateProductGrid(products) {
    const container = this.#component.refs.predictiveSearchResults;
    const productList = container.querySelector(
      '.predictive-search-results__wrapper-products'
    );
    if (!productList) return;

    productList.innerHTML = this.#renderProductItems(products);
    this.#attachProductClickListeners(container);
  }

  // -------------------------------------------------------------------------
  // GTM dataLayer
  // -------------------------------------------------------------------------

  #pushAutocompleteQueryGTM(query, suggestData, autocompleteData) {
    window.dataLayer = window.dataLayer || [];

    const suggestCount =
      (suggestData?.alternatives?.length || 0) +
      (suggestData?.suggested ? 1 : 0);
    const productCount = autocompleteData?.results?.length || 0;

    window.dataLayer.push({
      event: 'ss_autocomplete_query',
      ss_search_query: query,
      ss_suggestion_count: suggestCount,
      ss_product_count: productCount,
    });
  }

  #pushAutocompleteClickGTM(detail) {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: 'ss_autocomplete_click',
      ss_search_query: detail.query || '',
      ss_product_id: detail.productId || '',
      ss_product_name: detail.name || '',
      ss_product_price: detail.price || '',
    });
  }

  #resetScroll(container) {
    requestAnimationFrame(() => {
      container
        .querySelector('.predictive-search-results__inner')
        ?.scrollTo(0, 0);
      container.scrollTo(0, 0);
    });
  }
}

// ===========================================================================
// Initialization
// ===========================================================================

// Use whenDefined instead of DOMContentLoaded to ensure our patch is applied
// BEFORE Horizon's requestIdleCallback fires (which renders the empty state).
// whenDefined resolves as a microtask right after customElements.define(),
// which runs before any macrotask like requestIdleCallback.
customElements.whenDefined('predictive-search-component').then(() => {
  // Use ss-autocomplete-config (not ss-config) to avoid duplicate ID conflicts
  // on collection/search pages where ss-config is already rendered by the section
  const configEl = document.getElementById('ss-autocomplete-config');
  if (!configEl) return;

  try {
    const config = JSON.parse(configEl.textContent);
    if (!config.enabled || !config.siteId) return;

    const autocomplete = new SSAutocomplete(config);
    autocomplete.init();
  } catch (err) {
    console.error('[SS Autocomplete] Init failed:', err);
  }
});
